'use client'

import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { useELendVaultExposureSummaries } from '@/features/elend-details/logic/useELendVaultExposureSummaries'
import { getPortfolioHoldingsData, usePortfolioEvmBalances } from '@/features/portfolio'
import { VirtualMarketTable } from '@/ui/organisms/market-entry/VirtualMarketTable'
import type { MarketEntrySections, MarketEntryViewModel } from '@/ui/organisms/market-entry/types'
import { type PointsDisplayInfo, resolvePointsDisplay } from '@/ui/organisms/market-entry/utils/resolvePointsDisplay'
import { useBreakpoint } from '@/ui/utils/useBreakpoint'
import { useCallback, useMemo } from 'react'
import { toMarketEntryViewModel } from '../adapters'
import type { YieldOpportunity } from '../types'
import { getPointsType, getUtilization } from '../utils'
import { HoldingsCellBase } from './HoldingsCell'
import { UnifiedEarnMobileList } from './UnifiedEarnMobileList'
import { UtilizationCell } from './UtilizationCell'

interface UnifiedEarnVirtualListProps {
  opportunities: YieldOpportunity[]
}

export function UnifiedEarnVirtualList({ opportunities }: UnifiedEarnVirtualListProps) {
  const isDesktop = useBreakpoint('lg')
  const { getByAddress } = usePointsData()
  const portfolio = usePortfolioEvmBalances()

  const elendVaultAddresses = useMemo(
    () => opportunities.filter((opp) => opp.source === 'elend').map((opp) => opp.address),
    [opportunities],
  )
  const elendExposureSummaries = useELendVaultExposureSummaries(elendVaultAddresses)

  const entriesWithOpportunity = useMemo(
    () =>
      opportunities.map((opp) => {
        const entry = toMarketEntryViewModel(opp)
        const marketKey = opp.market?.key ?? opp.sourceData.type
        const stableEntryId = `${entry.id}-${entry.href}-${marketKey}`

        let { collateralOnly } = entry
        if (opp.source === 'elend') {
          const summary = elendExposureSummaries.get(opp.address.toLowerCase())
          if (summary) {
            collateralOnly = summary.borrowableCount === 0 && summary.collateralCount > 0
          }
        }

        return {
          entry: {
            ...entry,
            id: stableEntryId,
            collateralOnly,
          },
          opp,
        }
      }),
    [opportunities, elendExposureSummaries],
  )

  const viewModels = useMemo(() => entriesWithOpportunity.map(({ entry }) => entry), [entriesWithOpportunity])

  const opportunityMap = useMemo(() => {
    const map = new Map<string, YieldOpportunity>()
    for (const { entry, opp } of entriesWithOpportunity) {
      map.set(entry.id, opp)
    }
    return map
  }, [entriesWithOpportunity])

  const renderHoldings = useCallback(
    (entry: MarketEntryViewModel) => {
      const opp = opportunityMap.get(entry.id)
      if (!opp) return null
      return <HoldingsCellBase {...getPortfolioHoldingsData(opp, portfolio)} />
    },
    [opportunityMap, portfolio],
  )

  const renderUtilization = useCallback(
    (entry: MarketEntryViewModel) => {
      const opp = opportunityMap.get(entry.id)
      if (!opp) return null
      const util = getUtilization(opp)
      return <UtilizationCell utilization={util} source={opp.source} />
    },
    [opportunityMap],
  )

  const getPointsInfo = useCallback(
    (entryId: string): PointsDisplayInfo | undefined => {
      const opp = opportunityMap.get(entryId)
      if (!opp) return undefined

      const pointsType = getPointsType(opp.source)
      const points = getByAddress({
        type: pointsType,
        action: 'deposit',
        address: opp.address,
      })
      const depositOrLendContextPoints = opp.partnerPoints?.filter((p) => p.lend || p.deposit || p.collateral)
      const pointsInfo: PointsDisplayInfo = {
        multiplier: points?.multiplier,
        partnerPoints: depositOrLendContextPoints,
      }
      const { hasHypurrPoints, hasPartnerPoints } = resolvePointsDisplay({
        pointsInfo,
        isEarnVault: opp.source === 'evault',
      })

      if (!hasHypurrPoints && !hasPartnerPoints) return undefined

      return pointsInfo
    },
    [opportunityMap, getByAddress],
  )

  const sections: MarketEntrySections = useMemo(
    () => ({
      showCurator: false,
      showExposure: false,
      showUtilization: true,
      showTvl: true,
      show30dApy: true,
      showUserPosition: false,
      showWalletBalance: false,
      showHoldings: true,
      showBorrowDetails: false,
    }),
    [],
  )

  if (!isDesktop) {
    return (
      <UnifiedEarnMobileList
        opportunities={opportunities}
        elendExposureSummaries={elendExposureSummaries}
        portfolio={portfolio}
      />
    )
  }

  return (
    <VirtualMarketTable
      entries={viewModels}
      sections={sections}
      rowHeight={72}
      renderUtilization={renderUtilization}
      renderHoldings={renderHoldings}
      getPointsInfo={getPointsInfo}
    />
  )
}
